import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, InputGroup, Form } from 'react-bootstrap';
import clientMinorService from '../services/clientMinorService';
import ProtectedSection from './ProtectedSection';
import ClientMinorModal from './ClientMinorModal';

export default function ClientMinorListModal({ show, onHide, parent }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [edit, setEdit] = useState(null); // null = create, objet = edit
  const [error, setError] = useState('');

  const load = async () => {
    if (!parent?.id) return;
    try {
      const list = await clientMinorService.listByParent(parent.id);
      setItems(list);
      setError('');
    } catch (e) {
      setError('Chargement impossible.');
    }
  };

  useEffect(() => { if (show) load(); }, [show]); // eslint-disable-line

  const normalized = (s) => (s || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu,'').toLowerCase();
  const filtered = useMemo(() => {
    const q = normalized(filter);
    if (!q) return items;
    return items.filter(it =>
      normalized(`${it.firstName} ${it.lastName} ${it.relation} ${it.email} ${it.phone}`).includes(q)
    );
  }, [items, filter]);

  const onCreate = () => setEdit({}); // open modal in create mode

  const onEdit = (it) => setEdit(it);

  const onRemove = async (it) => {
    if (!window.confirm(`Supprimer ${it.firstName} ${it.lastName} ?`)) return;
    try {
      await clientMinorService.remove(it.id);
      await load();
    } catch (e) {
      setError('Suppression impossible.');
    }
  };

  const onSave = async (values) => {
    try {
      if (edit?.id) {
        await clientMinorService.update(edit.id, values);
      } else {
        await clientMinorService.create(parent.id, values);
      }
      setEdit(null);
      await load();
    } catch (e) {
      setError('Enregistrement impossible.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Comptes mineurs — {parent?.firstName} {parent?.lastName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <InputGroup style={{ maxWidth: 280 }}>
            <Form.Control placeholder="Rechercher…" value={filter} onChange={e => setFilter(e.target.value)} />
            <Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button>
          </InputGroup>
          <ProtectedSection code="CLIENT_MINOR_CREATE">
            <Button onClick={onCreate}>+ Ajouter un mineur</Button>
          </ProtectedSection>
        </div>

        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Prénom</th>
              <th>Nom</th>
              <th>Naissance</th>
              <th>Lien</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Actif</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(it => (
              <tr key={it.id}>
                <td>{it.firstName}</td>
                <td>{it.lastName}</td>
                <td>{it.birthDate || '—'}</td>
                <td>{it.relation || '—'}</td>
                <td>{it.email || '—'}</td>
                <td>{it.phone || '—'}</td>
                <td>{it.active ? 'Oui' : 'Non'}</td>
                <td>
                  <ProtectedSection code="CLIENT_MINOR_UPDATE">
                    <Button className="me-2" size="sm" variant="outline-primary" onClick={() => onEdit(it)}>Modifier</Button>
                  </ProtectedSection>
                  <ProtectedSection code="CLIENT_MINOR_DELETE">
                    <Button size="sm" variant="outline-danger" onClick={() => onRemove(it)}>Supprimer</Button>
                  </ProtectedSection>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted">Aucun mineur.</td></tr>
            )}
          </tbody>
        </Table>

        {/* sous-modale de création/édition */}
        {edit !== null && (
          <ClientMinorModal
            show={true}
            onHide={() => setEdit(null)}
            onSave={onSave}
            initialData={edit || {}}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}
